import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const slides = [
  {
    heading: "Empowering Your Success with Cutting-Edge AI Solutions",
    description:
      "Harness AI-driven creativity to innovate and lead in your industry. Discover the future of digital transformation.",
    cta: "Explore AI Services",
    href: "/services/generative-ai",
    bg: "/assets/banner-2.jpg",
  },
  {
    heading: "Transform Your Business with Digital Solutions",
    description:
      "Employ cutting-edge technologies to unlock the full potential of your operations and drive innovation.",
    cta: "Discover Transformation",
    href: "/services/digital-transformation",
    bg: "/assets/banner-dt.jpg",
  },
  {
    heading: "Sign Documents Seamlessly with Signulu",
    description:
      "Experience secure, advanced eSignature solutions that empower your business transactions anywhere, anytime.",
    cta: "Try Signulu Free",
    href: "https://app.signulu.com/account/register",
    bg: "/assets/banner-signulu.jpg",
  },
  {
    heading: "Streamline Hiring with Attainica",
    description:
      "Automate your recruitment process using AI to find the perfect candidates efficiently and effectively.",
    cta: "Learn More About Attainica",
    href: "https://www.attainica.com/",
    bg: "/assets/banner-attainica.jpg",
  },
];

const MaskedHeading = ({ text }) => (
  <h1 className="font-heading font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-3xl">
    {text.split(" ").map((word, i) => (
      <span key={i} className="inline-block overflow-hidden align-top pb-1 -mb-1">
        <motion.span
          className="inline-block will-change-transform"
          initial={{ y: "115%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.75, delay: 0.15 + i * 0.045, ease: [0.22, 1, 0.36, 1] }}
        >
          {word}
          {i < text.split(" ").length - 1 ? "\u00A0" : ""}
        </motion.span>
      </span>
    ))}
  </h1>
);

const HeroSlider = () => {
  const autoplay = useRef(Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true }));
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 32 }, [autoplay.current]);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section ref={sectionRef} data-testid="hero-slider" className="relative h-screen min-h-[640px] hero-grain">
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, i) => (
            <div key={i} className="relative min-w-0 flex-[0_0_100%] h-full" data-testid={`hero-slide-${i + 1}`}>
              <div className="absolute inset-0 overflow-hidden bg-miti-navy">
                <img
                  src={slide.bg}
                  alt=""
                  className={`w-full h-full object-cover ${i === selected ? "ken-burns-active" : "scale-110"}`}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-miti-navy/70 via-miti-navy/25 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-miti-navy/60 via-transparent to-miti-navy/20" />
              </div>

              <motion.div
                style={{ y: contentY, opacity: contentOpacity }}
                className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center"
              >
                {i === selected && (
                  <div key={selected}>
                    <motion.span
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.05 }}
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-md px-4 py-1.5 text-xs font-semibold tracking-[0.2em] text-sky-300 uppercase mb-7"
                      data-testid="hero-slide-badge"
                    >
                      Millennium Info Tech
                    </motion.span>
                    <MaskedHeading text={slide.heading} />
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7, delay: 0.65 }}
                      className="mt-6 max-w-xl text-base md:text-lg text-slate-300"
                      data-testid="hero-slide-description"
                    >
                      {slide.description}
                    </motion.p>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7, delay: 0.8 }}
                      className="mt-9"
                    >
                      <a
                        href={slide.href}
                        data-testid={`hero-slide-cta-${i + 1}`}
                        className="group inline-flex items-center gap-3 rounded-full border-2 border-white/80 px-7 py-3.5 text-sm font-semibold text-white hover:bg-miti-blue hover:border-miti-blue transition-all duration-300"
                      >
                        {slide.cta}
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </a>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      <button
        data-testid="hero-prev-arrow"
        onClick={() => emblaApi && emblaApi.scrollPrev()}
        aria-label="Previous slide"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full border border-white/25 bg-white/10 backdrop-blur-md items-center justify-center text-white hover:bg-miti-blue hover:border-miti-blue transition-all duration-300"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        data-testid="hero-next-arrow"
        onClick={() => emblaApi && emblaApi.scrollNext()}
        aria-label="Next slide"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full border border-white/25 bg-white/10 backdrop-blur-md items-center justify-center text-white hover:bg-miti-blue hover:border-miti-blue transition-all duration-300"
      >
        <ChevronRight size={20} />
      </button>

      <div className="absolute bottom-8 inset-x-0 z-20 max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="hero-dots">
          {snaps.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i + 1}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === selected ? "w-10 bg-sky-400" : "w-4 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
        <span data-testid="hero-slide-counter" className="font-heading text-sm tracking-[0.25em] text-white/70">
          {String(selected + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
        </span>
      </div>
    </section>
  );
};

export default HeroSlider;
